// The constellations drifting across the home sky: fourteen real figures
// drawn in the atlas style of the Figma Sky component (Atlas figures, 65:1615). Positions
// are the stars' J2000 right ascension and declination in degrees, rounded,
// as listed in the Yale Bright Star Catalogue; the stick figures are the
// usual ones. Each figure is projected around its own center, as a star map
// would draw it (east to the left), and placed along the wrapping field in
// the home half, above the horizon.
import { FIELD_WIDTH, HALF_HEIGHT } from './weekdaySkies';

interface CatalogStar {
  name: string;
  ra: number;
  dec: number;
  mag: number;
}

interface FigureSource {
  name: string;
  stars: CatalogStar[];
  lines: Array<[string, string]>;
}

const SOURCES: FigureSource[] = [
  {
    name: 'Cassiopeia',
    stars: [
      { name: 'Caph', ra: 2.29, dec: 59.15, mag: 2.27 },
      { name: 'Schedar', ra: 10.13, dec: 56.54, mag: 2.24 },
      { name: 'Navi', ra: 14.18, dec: 60.72, mag: 2.47 },
      { name: 'Ruchbah', ra: 21.45, dec: 60.24, mag: 2.68 },
      { name: 'Segin', ra: 28.6, dec: 63.67, mag: 3.37 },
    ],
    lines: [
      ['Caph', 'Schedar'],
      ['Schedar', 'Navi'],
      ['Navi', 'Ruchbah'],
      ['Ruchbah', 'Segin'],
    ],
  },
  {
    name: 'Orion',
    stars: [
      { name: 'Betelgeuse', ra: 88.79, dec: 7.41, mag: 0.5 },
      { name: 'Rigel', ra: 78.63, dec: -8.2, mag: 0.13 },
      { name: 'Bellatrix', ra: 81.28, dec: 6.35, mag: 1.64 },
      { name: 'Mintaka', ra: 83.0, dec: -0.3, mag: 2.23 },
      { name: 'Alnilam', ra: 84.05, dec: -1.2, mag: 1.69 },
      { name: 'Alnitak', ra: 85.19, dec: -1.94, mag: 1.77 },
      { name: 'Saiph', ra: 86.94, dec: -9.67, mag: 2.07 },
      { name: 'Meissa', ra: 83.78, dec: 9.93, mag: 3.39 },
    ],
    lines: [
      ['Meissa', 'Betelgeuse'],
      ['Meissa', 'Bellatrix'],
      ['Betelgeuse', 'Alnitak'],
      ['Bellatrix', 'Mintaka'],
      ['Mintaka', 'Alnilam'],
      ['Alnilam', 'Alnitak'],
      ['Alnitak', 'Saiph'],
      ['Mintaka', 'Rigel'],
    ],
  },
  {
    name: 'Lyra',
    stars: [
      { name: 'Vega', ra: 279.23, dec: 38.78, mag: 0.03 },
      { name: 'Epsilon', ra: 281.08, dec: 39.67, mag: 4.7 },
      { name: 'Zeta', ra: 281.19, dec: 37.61, mag: 4.36 },
      { name: 'Delta', ra: 283.63, dec: 36.9, mag: 4.3 },
      { name: 'Sulafat', ra: 284.74, dec: 32.69, mag: 3.25 },
      { name: 'Sheliak', ra: 282.52, dec: 33.36, mag: 3.52 },
    ],
    lines: [
      ['Vega', 'Epsilon'],
      ['Vega', 'Zeta'],
      ['Zeta', 'Delta'],
      ['Delta', 'Sulafat'],
      ['Sulafat', 'Sheliak'],
      ['Sheliak', 'Zeta'],
    ],
  },
  {
    name: 'Ursa Major',
    stars: [
      { name: 'Dubhe', ra: 165.93, dec: 61.75, mag: 1.79 },
      { name: 'Merak', ra: 165.46, dec: 56.38, mag: 2.37 },
      { name: 'Phecda', ra: 178.46, dec: 53.69, mag: 2.44 },
      { name: 'Megrez', ra: 183.86, dec: 57.03, mag: 3.31 },
      { name: 'Alioth', ra: 193.51, dec: 55.96, mag: 1.77 },
      { name: 'Mizar', ra: 200.98, dec: 54.93, mag: 2.27 },
      { name: 'Alkaid', ra: 206.89, dec: 49.31, mag: 1.86 },
    ],
    lines: [
      ['Dubhe', 'Merak'],
      ['Merak', 'Phecda'],
      ['Phecda', 'Megrez'],
      ['Megrez', 'Dubhe'],
      ['Megrez', 'Alioth'],
      ['Alioth', 'Mizar'],
      ['Mizar', 'Alkaid'],
    ],
  },
  {
    name: 'Cygnus',
    stars: [
      { name: 'Deneb', ra: 310.36, dec: 45.28, mag: 1.25 },
      { name: 'Sadr', ra: 305.56, dec: 40.26, mag: 2.23 },
      { name: 'Albireo', ra: 292.68, dec: 27.96, mag: 3.05 },
      { name: 'Gienah', ra: 311.55, dec: 33.97, mag: 2.48 },
      { name: 'Fawaris', ra: 296.24, dec: 45.13, mag: 2.87 },
    ],
    lines: [
      ['Deneb', 'Sadr'],
      ['Sadr', 'Albireo'],
      ['Fawaris', 'Sadr'],
      ['Sadr', 'Gienah'],
    ],
  },
  {
    name: 'Leo',
    stars: [
      { name: 'Regulus', ra: 152.09, dec: 11.97, mag: 1.35 },
      { name: 'Eta', ra: 151.83, dec: 16.76, mag: 3.5 },
      { name: 'Algieba', ra: 154.99, dec: 19.84, mag: 2.08 },
      { name: 'Adhafera', ra: 154.17, dec: 23.42, mag: 3.44 },
      { name: 'Rasalas', ra: 148.19, dec: 26.01, mag: 3.88 },
      { name: 'Epsilon', ra: 146.46, dec: 23.77, mag: 2.98 },
      { name: 'Zosma', ra: 168.53, dec: 20.52, mag: 2.56 },
      { name: 'Chertan', ra: 168.56, dec: 15.43, mag: 3.33 },
      { name: 'Denebola', ra: 177.26, dec: 14.57, mag: 2.14 },
    ],
    lines: [
      ['Regulus', 'Eta'],
      ['Eta', 'Algieba'],
      ['Algieba', 'Adhafera'],
      ['Adhafera', 'Rasalas'],
      ['Rasalas', 'Epsilon'],
      ['Algieba', 'Zosma'],
      ['Zosma', 'Denebola'],
      ['Denebola', 'Chertan'],
      ['Chertan', 'Zosma'],
      ['Chertan', 'Regulus'],
    ],
  },
  {
    name: 'Scorpius',
    stars: [
      { name: 'Acrab', ra: 241.36, dec: -19.81, mag: 2.62 },
      { name: 'Dschubba', ra: 240.08, dec: -22.62, mag: 2.29 },
      { name: 'Fang', ra: 239.71, dec: -26.11, mag: 2.89 },
      { name: 'Alniyat', ra: 245.3, dec: -25.59, mag: 2.88 },
      { name: 'Antares', ra: 247.35, dec: -26.43, mag: 1.06 },
      { name: 'Tau', ra: 248.97, dec: -28.22, mag: 2.82 },
      { name: 'Larawag', ra: 252.54, dec: -34.29, mag: 2.29 },
      { name: 'Mu', ra: 252.97, dec: -38.05, mag: 3.0 },
      { name: 'Zeta', ra: 253.65, dec: -42.36, mag: 3.6 },
      { name: 'Eta', ra: 258.04, dec: -43.24, mag: 3.33 },
      { name: 'Sargas', ra: 264.33, dec: -43.0, mag: 1.86 },
      { name: 'Iota', ra: 266.89, dec: -40.13, mag: 3.0 },
      { name: 'Girtab', ra: 265.62, dec: -39.03, mag: 2.39 },
      { name: 'Shaula', ra: 263.4, dec: -37.1, mag: 1.62 },
      { name: 'Lesath', ra: 262.69, dec: -37.3, mag: 2.7 },
    ],
    lines: [
      ['Acrab', 'Dschubba'],
      ['Dschubba', 'Fang'],
      ['Dschubba', 'Alniyat'],
      ['Alniyat', 'Antares'],
      ['Antares', 'Tau'],
      ['Tau', 'Larawag'],
      ['Larawag', 'Mu'],
      ['Mu', 'Zeta'],
      ['Zeta', 'Eta'],
      ['Eta', 'Sargas'],
      ['Sargas', 'Iota'],
      ['Iota', 'Girtab'],
      ['Girtab', 'Shaula'],
      ['Shaula', 'Lesath'],
    ],
  },
  {
    name: 'Corona Borealis',
    stars: [
      { name: 'Theta', ra: 233.23, dec: 31.36, mag: 4.14 },
      { name: 'Nusakan', ra: 231.96, dec: 29.11, mag: 3.68 },
      { name: 'Alphecca', ra: 233.67, dec: 26.71, mag: 2.23 },
      { name: 'Gamma', ra: 235.69, dec: 26.3, mag: 3.84 },
      { name: 'Delta', ra: 237.4, dec: 26.07, mag: 4.63 },
      { name: 'Epsilon', ra: 239.4, dec: 26.88, mag: 4.15 },
      { name: 'Iota', ra: 240.36, dec: 29.85, mag: 4.98 },
    ],
    lines: [
      ['Theta', 'Nusakan'],
      ['Nusakan', 'Alphecca'],
      ['Alphecca', 'Gamma'],
      ['Gamma', 'Delta'],
      ['Delta', 'Epsilon'],
      ['Epsilon', 'Iota'],
    ],
  },
  {
    name: 'Bootes',
    stars: [
      { name: 'Arcturus', ra: 213.92, dec: 19.18, mag: -0.05 },
      { name: 'Muphrid', ra: 208.67, dec: 18.4, mag: 2.68 },
      { name: 'Izar', ra: 221.25, dec: 27.07, mag: 2.37 },
      { name: 'Delta', ra: 228.88, dec: 33.31, mag: 3.47 },
      { name: 'Nekkar', ra: 225.49, dec: 40.39, mag: 3.5 },
      { name: 'Seginus', ra: 218.02, dec: 38.31, mag: 3.03 },
      { name: 'Rho', ra: 217.96, dec: 30.37, mag: 3.58 },
      { name: 'Zeta', ra: 220.29, dec: 13.73, mag: 3.78 },
    ],
    lines: [
      ['Arcturus', 'Izar'],
      ['Izar', 'Delta'],
      ['Delta', 'Nekkar'],
      ['Nekkar', 'Seginus'],
      ['Seginus', 'Rho'],
      ['Rho', 'Arcturus'],
      ['Arcturus', 'Muphrid'],
      ['Arcturus', 'Zeta'],
    ],
  },
  {
    name: 'Aquila',
    stars: [
      { name: 'Altair', ra: 297.7, dec: 8.87, mag: 0.76 },
      { name: 'Tarazed', ra: 296.56, dec: 10.61, mag: 2.72 },
      { name: 'Alshain', ra: 298.83, dec: 6.41, mag: 3.71 },
      { name: 'Delta', ra: 291.37, dec: 3.11, mag: 3.36 },
      { name: 'Zeta', ra: 286.35, dec: 13.86, mag: 2.99 },
      { name: 'Theta', ra: 302.83, dec: -0.82, mag: 3.26 },
      { name: 'Lambda', ra: 286.56, dec: -4.88, mag: 3.43 },
    ],
    lines: [
      ['Tarazed', 'Altair'],
      ['Altair', 'Alshain'],
      ['Alshain', 'Theta'],
      ['Altair', 'Delta'],
      ['Delta', 'Lambda'],
      ['Delta', 'Zeta'],
    ],
  },
  {
    name: 'Gemini',
    stars: [
      { name: 'Castor', ra: 113.65, dec: 31.89, mag: 1.58 },
      { name: 'Pollux', ra: 116.33, dec: 28.03, mag: 1.14 },
      { name: 'Tau', ra: 107.79, dec: 30.25, mag: 4.41 },
      { name: 'Mebsuta', ra: 100.98, dec: 25.13, mag: 2.98 },
      { name: 'Tejat', ra: 95.74, dec: 22.51, mag: 2.88 },
      { name: 'Propus', ra: 93.72, dec: 22.51, mag: 3.28 },
      { name: 'Wasat', ra: 110.03, dec: 21.98, mag: 3.53 },
      { name: 'Mekbuda', ra: 106.03, dec: 20.57, mag: 3.79 },
      { name: 'Alhena', ra: 99.43, dec: 16.4, mag: 1.93 },
    ],
    lines: [
      ['Castor', 'Pollux'],
      ['Castor', 'Tau'],
      ['Tau', 'Mebsuta'],
      ['Mebsuta', 'Tejat'],
      ['Tejat', 'Propus'],
      ['Tau', 'Wasat'],
      ['Pollux', 'Wasat'],
      ['Wasat', 'Mekbuda'],
      ['Mekbuda', 'Alhena'],
    ],
  },
  {
    name: 'Canis Major',
    stars: [
      { name: 'Sirius', ra: 101.29, dec: -16.72, mag: -1.46 },
      { name: 'Mirzam', ra: 95.68, dec: -17.96, mag: 1.98 },
      { name: 'Muliphein', ra: 105.94, dec: -15.63, mag: 4.12 },
      { name: 'Omicron', ra: 105.76, dec: -23.83, mag: 3.02 },
      { name: 'Wezen', ra: 107.1, dec: -26.39, mag: 1.84 },
      { name: 'Adhara', ra: 104.66, dec: -28.97, mag: 1.5 },
      { name: 'Aludra', ra: 111.02, dec: -29.3, mag: 2.45 },
      { name: 'Furud', ra: 95.08, dec: -30.06, mag: 3.02 },
    ],
    lines: [
      ['Mirzam', 'Sirius'],
      ['Sirius', 'Muliphein'],
      ['Sirius', 'Omicron'],
      ['Omicron', 'Wezen'],
      ['Wezen', 'Adhara'],
      ['Adhara', 'Furud'],
      ['Wezen', 'Aludra'],
    ],
  },
  {
    name: 'Sagittarius',
    stars: [
      { name: 'Alnasl', ra: 271.45, dec: -30.42, mag: 2.98 },
      { name: 'Kaus Media', ra: 275.25, dec: -29.83, mag: 2.7 },
      { name: 'Kaus Australis', ra: 276.04, dec: -34.38, mag: 1.85 },
      { name: 'Kaus Borealis', ra: 277.0, dec: -25.42, mag: 2.81 },
      { name: 'Phi', ra: 281.41, dec: -26.99, mag: 3.17 },
      { name: 'Nunki', ra: 283.82, dec: -26.3, mag: 2.05 },
      { name: 'Ascella', ra: 285.65, dec: -29.88, mag: 2.6 },
      { name: 'Tau', ra: 286.74, dec: -27.67, mag: 3.32 },
    ],
    lines: [
      ['Alnasl', 'Kaus Media'],
      ['Alnasl', 'Kaus Australis'],
      ['Kaus Media', 'Kaus Australis'],
      ['Kaus Media', 'Kaus Borealis'],
      ['Kaus Borealis', 'Phi'],
      ['Kaus Media', 'Phi'],
      ['Phi', 'Ascella'],
      ['Ascella', 'Kaus Australis'],
      ['Phi', 'Nunki'],
      ['Nunki', 'Tau'],
      ['Tau', 'Ascella'],
    ],
  },
  {
    name: 'Pegasus',
    stars: [
      { name: 'Markab', ra: 346.19, dec: 15.21, mag: 2.48 },
      { name: 'Scheat', ra: 345.94, dec: 28.08, mag: 2.42 },
      { name: 'Alpheratz', ra: 2.1, dec: 29.09, mag: 2.06 },
      { name: 'Algenib', ra: 3.31, dec: 15.18, mag: 2.83 },
      { name: 'Homam', ra: 340.37, dec: 10.83, mag: 3.4 },
    ],
    lines: [
      ['Markab', 'Scheat'],
      ['Scheat', 'Alpheratz'],
      ['Alpheratz', 'Algenib'],
      ['Algenib', 'Markab'],
      ['Markab', 'Homam'],
    ],
  },
];

/** Sky units per degree of sky: the Big Dipper comes out about 460 units wide, like the Figma figures. */
export const UNITS_PER_DEGREE = 18;

export interface AtlasFigure {
  name: string;
  /** Center in the field, sky units. */
  x: number;
  y: number;
  /** Stars relative to the center, sky units, with their magnitudes. */
  stars: Array<{ x: number; y: number; mag: number }>;
  lines: Array<[number, number]>;
  /** Half the bounding box, sky units. */
  halfWidth: number;
  halfHeight: number;
}

const DEG = Math.PI / 180;

/**
 * Gnomonic projection around the figure's mean position; east (higher RA)
 * to the left, north up. Right ascensions are taken relative to the first
 * star, so a figure across 0h (Pegasus) stays in one piece.
 */
export function projectFigure(source: FigureSource): Omit<AtlasFigure, 'x' | 'y'> {
  const first = source.stars[0]!.ra;
  const unwrapped = source.stars.map((s) => s.ra + (s.ra - first > 180 ? -360 : s.ra - first < -180 ? 360 : 0));
  const ra0 = (unwrapped.reduce((t, ra) => t + ra, 0) / unwrapped.length) * DEG;
  const dec0 = (source.stars.reduce((t, s) => t + s.dec, 0) / source.stars.length) * DEG;
  const raw = source.stars.map((s, i) => {
    const ra = unwrapped[i]! * DEG;
    const dec = s.dec * DEG;
    const cosc = Math.sin(dec0) * Math.sin(dec) + Math.cos(dec0) * Math.cos(dec) * Math.cos(ra - ra0);
    const x = (Math.cos(dec) * Math.sin(ra - ra0)) / cosc;
    const y = (Math.cos(dec0) * Math.sin(dec) - Math.sin(dec0) * Math.cos(dec) * Math.cos(ra - ra0)) / cosc;
    const units = UNITS_PER_DEGREE / DEG;
    return { x: -x * units, y: -y * units, mag: s.mag };
  });
  // Center the figure on its bounding box rather than its mean.
  const xs = raw.map((s) => s.x);
  const ys = raw.map((s) => s.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  const stars = raw.map((s) => ({ x: s.x - cx, y: s.y - cy, mag: s.mag }));
  const index = new Map(source.stars.map((s, i) => [s.name, i]));
  return {
    name: source.name,
    stars,
    lines: source.lines.map(([a, b]) => [index.get(a)!, index.get(b)!]),
    halfWidth: (Math.max(...xs) - Math.min(...xs)) / 2,
    halfHeight: (Math.max(...ys) - Math.min(...ys)) / 2,
  };
}

/**
 * Spread along the field and over the whole home half, from just under the
 * top edge to just above the hills (field y 1000–2000 is home), alternating
 * high and low so about three are in view at once.
 */
const PLACEMENT: Array<{ name: string; x: number; y: number }> = [
  { name: 'Cassiopeia', x: 312, y: 150 },
  { name: 'Canis Major', x: 504, y: 700 },
  { name: 'Orion', x: 864, y: 400 },
  { name: 'Lyra', x: 1200, y: 130 },
  { name: 'Sagittarius', x: 1296, y: 760 },
  { name: 'Ursa Major', x: 1704, y: 300 },
  { name: 'Corona Borealis', x: 2136, y: 620 },
  { name: 'Cygnus', x: 2472, y: 330 },
  { name: 'Aquila', x: 2940, y: 660 },
  { name: 'Pegasus', x: 3384, y: 230 },
  { name: 'Leo', x: 3960, y: 600 },
  { name: 'Gemini', x: 4560, y: 230 },
  { name: 'Scorpius', x: 5160, y: 610 },
  { name: 'Bootes', x: 5760, y: 330 },
];

export const ATLAS_FIGURES: AtlasFigure[] = PLACEMENT.map((p) => {
  const source = SOURCES.find((s) => s.name === p.name)!;
  return { ...projectFigure(source), x: p.x % FIELD_WIDTH, y: HALF_HEIGHT + p.y };
});

/** The atlas star symbol's outer radius in px for a magnitude: brighter stars draw larger. */
export function atlasStarRadius(mag: number, size: number): number {
  return size * Math.max(2, Math.min(4.5, 4.6 - 0.75 * mag));
}
